import { HTTP_INTERCEPTORS, HttpBackend } from '@angular/common/http';
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  Inject,
  ModuleWithProviders,
  NgModule,
  Optional,
  Provider,
  SkipSelf,
} from '@angular/core';

import { Title } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import {
  TranslateLoader,
  TranslateModule,
  TranslateModuleConfig,
  TranslateService,
} from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { MessageService, SelectItem } from 'primeng/api';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from '../../azure-ad/azure-ad.module';
import { CommonUtils, LocalStorageService } from '../../common/public-api';
import {
  CacheService,
  MEMORY_CACHE_SERVICE,
} from '../../common/services/cache.service';
import { ApimInterceptor } from '../../core/interceptor/apim.interceptor';
import { NgswBypassInterceptor } from '../../core/interceptor/ngsw-bypass.interceptor';
import {
  AUTHENTICATION_SERVICE,
  AppGuard,
  AuthGuard,
  DeactiveGuard,
  FormAuthenticationService,
  SIGVCoreModule,
} from '../../core/public-api';
import { SharedModule } from '../../shared/shared.module';
import { AppService } from '../services/app.service';
import { HttpErrorInterceptor } from '../services/error/http-error.interceptor';
import { AuditInfoInterceptor } from '../utils/sigv-service/audit-info.interceptor';
import { HttpResponseInterceptor } from '../utils/sigv-service/http-response-info.interceptor';
import { BuildinPageModule } from './buildin-page/buildin-page.module';
import { DropdownModule } from './dropdown/dropdown.module';
import { MainLayoutModule } from './layout/components/main-layout/layout.module';
import { LoaderInterceptor } from './loader/loader.interceptor';
import { UIConfig } from './models/ui.config';
import { SearchFormModule } from './search-form/search-form.module';
import { DEFAULT_LANGUAGE, LANGUAGE_LOCALE_OPTIONS } from './variables';
import { WaterMarkModule } from './watermark/watermark';
import { WidgetModule } from './widget/widget.module';

const defaultLanguageLocale = 'en-us';
const defaultLanguageOptions: SelectItem[] = [
  { label: 'English', value: 'en-us' },
  { label: '中文(繁體)', value: 'zh-tw' },
];

export const BUILDIN_UI_MODULES = [
  BuildinPageModule,
  DropdownModule,
  // LayoutModule,
  MainLayoutModule,
  SearchFormModule,
  WaterMarkModule,
  WidgetModule,
];

@NgModule({
  imports: [
    TranslateModule,
    SIGVCoreModule,
    DropdownModule,
    // LayoutModule,
    MainLayoutModule,
    SearchFormModule,
    WaterMarkModule,
    WidgetModule,
    BuildinPageModule,
    SharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled:
        (globalThis.sigv?.appContext?.sigvRetailConfig?.enabledServiceWorker ?? false) ||
        location.protocol.startsWith('https') ||
        ['localhost', '127.0.0.1'].includes(location.hostname),
      registrationStrategy: 'registerImmediately',
    }),
  ],
  exports: [...BUILDIN_UI_MODULES],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuditInfoInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApimInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NgswBypassInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpResponseInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: DEFAULT_LANGUAGE,
      useValue: defaultLanguageLocale,
    },
    {
      provide: LANGUAGE_LOCALE_OPTIONS,
      useValue: defaultLanguageOptions,
    },
    {
      provide: MEMORY_CACHE_SERVICE,
      useFactory: cacheServiceFactory,
      deps: [LocalStorageService],
    },
    {
      provide: AUTHENTICATION_SERVICE,
      useClass: FormAuthenticationService,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialApp,
      deps: [AppService, NGXLogger],
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    AuthGuard,
    DeactiveGuard,
    AppGuard,
  ],
})
export class SIGVUIModule {
  constructor(
    @Optional() @SkipSelf() parentModule: SIGVUIModule,
    translateService: TranslateService,
    @Inject(DEFAULT_LANGUAGE) defaultLang: string,
    private titleService: Title
  ) {
    if (parentModule) {
      throw new Error(
        `${
          new.target.name
        } is already loaded. Import in your base AppModule only.`
      );
    }
    const title = `${globalThis.sigv.appContext.projectConfig.systemName}`;
    this.titleService.setTitle(title);
    translateService.setDefaultLang(defaultLanguageLocale);
    translateService.currentLang = defaultLanguageLocale;
    console.log('default lang', defaultLang, translateService.currentLang);
    console.log('sys param', globalThis.sigv.appContext);
  }
  //此處將lib的語系與使用lib的app的語系檔合併
  static forRoot(uiConfig: UIConfig): ModuleWithProviders<SIGVUIModule> {
    const httpLoaderFactoryForCore = (httpBackend: HttpBackend) => {
      return new MultiTranslateHttpLoader(httpBackend, [
        //共用Common 語系對應
        { prefix: './assets/i18n/sigv-lib/common.', suffix: '.json' },
        //lib元件 語系對應
        { prefix: './assets/i18n/sigv-lib/lib.', suffix: '.json' },
        ...(uiConfig.translationResources ?? []),
      ]);
    };
    const config: TranslateModuleConfig = {
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactoryForCore,
        deps: [HttpBackend],
      },
    };

    const rootProviders: (Provider | EnvironmentProviders)[] = [
      ...(TranslateModule.forRoot(config).providers ?? []),
    ];

    if (
      CommonUtils.isNullOrUndefined(uiConfig.defaultLanguageLocale) === false
    ) {
      const lang = uiConfig.defaultLanguageLocale as string;
      rootProviders.push({
        provide: DEFAULT_LANGUAGE,
        useValue: lang,
      });
    }

    if (
      CommonUtils.isNullOrUndefined(uiConfig.languageLocaleOptions) === false
    ) {
      const langOptions = uiConfig.languageLocaleOptions as SelectItem[];
      rootProviders.push({
        provide: LANGUAGE_LOCALE_OPTIONS,
        useValue: langOptions,
      });
    }

    return {
      ngModule: SIGVUIModule,
      providers: rootProviders,
    };
  }
}
export function cacheServiceFactory(
  locaStorageService: LocalStorageService
): CacheService {
  return new CacheService(locaStorageService);
}
export function initialApp(configService: AppService, logger: NGXLogger): any {
  return () => {
    return configService
      .load()
      .then(() => logger.info('Load app information end'));
  };
}
